import { useEffect } from 'react';
import Handsontable, { Events } from 'handsontable/base';
import type { Action as HotUndoRedoAction } from 'handsontable/plugins/undoRedo';

import { TableChangeSource } from '../../../../common/table/TableTypes';
import {
    setPendingUndoStackChangeSource,
    startUndoRedoCellDataReplay,
    syncPendingUndoRedoActionToLatestUndoStackEntry,
    type UndoRedoRichCellDataState,
    type UndoRedoActionWithCellData,
} from '../utils/hotInstanceUtils/undoRedo/hotTableUndoRedoRichCellDataUtils';
import {
    HOT_TABLE_MOVE_ACTION_TYPE,
    type HotTableMoveUndoRedoAction,
} from '../utils/hotInstanceUtils/undoRedo/hotTableUndoRedoMoveUtils';
import {
    HOT_TABLE_COLUMN_RESIZE_ACTION_TYPE,
    type HotTableColumnResizeUndoRedoAction,
} from '../utils/hotInstanceUtils/undoRedo/hotTableUndoRedoColumnResizeUtils';

type FormulaEngineWithUndoRedo = {
    undo: () => void;
    redo: () => void;
};

type MilanoteHotUndoRedoAction = HotTableMoveUndoRedoAction | HotTableColumnResizeUndoRedoAction;
type HotUndoRedoHookAction = HotUndoRedoAction | MilanoteHotUndoRedoAction;
type FormulaEngineUndoRedoMethod = keyof FormulaEngineWithUndoRedo;

/**
 * Handsontable's hook types only describe its built-in action union, so custom undo actions need runtime checks.
 */
const isMilanoteHotUndoRedoAction = (action: HotUndoRedoHookAction): boolean =>
    action.actionType === HOT_TABLE_MOVE_ACTION_TYPE || action.actionType === HOT_TABLE_COLUMN_RESIZE_ACTION_TYPE;

/**
 * Prevents Handsontable's formulas plugin from replaying HyperFormula undo/redo for Milanote-only HOT actions.
 */
const suppressNextFormulaEngineUndoRedo = (hotInstance: Handsontable, method: FormulaEngineUndoRedoMethod): void => {
    const formulasPlugin = hotInstance.getPlugin<'formulas'>('formulas');
    const engine = formulasPlugin?.engine as FormulaEngineWithUndoRedo | undefined;
    if (!engine) return;

    // Get a reference to the original method
    const originalMethod = engine[method].bind(engine);

    // Replace it with a function that when run, does nothing except for returning the engine's method back to
    //  its original state. So it will essentially be a no-op for that invocation, but the next will be back to normal
    engine[method] = () => {
        engine[method] = originalMethod;
    };
};

/**
 * Creates a global HOT undo/redo hook that only suppresses HyperFormula for this table's Milanote-only HOT actions.
 */
const createFormulaEngineUndoRedoSuppressionHook = (
    hotInstance: Handsontable,
    method: FormulaEngineUndoRedoMethod,
): NonNullable<Events['beforeUndo']> =>
    function (this: Handsontable, action) {
        // Global hooks run before instance hooks, so they must ignore other HOT instances.
        if (this !== hotInstance) return;
        if (!isMilanoteHotUndoRedoAction(action)) return;

        suppressNextFormulaEngineUndoRedo(this, method);
    };

/**
 * Handsontable's `Hooks.remove` typing is narrower than `Hooks.add`, so remove the callback through a small wrapper.
 *
 * This also makes it very obvious that it's a global hook.
 */
const addGlobalHook = <K extends 'beforeUndo' | 'beforeRedo'>(key: K, callback: NonNullable<Events[K]>) => {
    Handsontable.hooks.add(key, callback);

    return () => {
        Handsontable.hooks.remove(key, callback as unknown as () => void);
    };
};

/**
 * Attaches the Handsontable undo/redo stack hooks used to preserve Milanote rich cell data.
 *
 * - Milanote stores rich data in the metadata of Handsontable cells, because Handsontable usually only
 *  works with primitive data in each cell's "value" property.
 * - So to support styles within the text, we add a "draftContent" property to the cell's metadata. This property
 *  is an object that stores the raw editor content object.
 * - Because Handsontable only works with primitive data - its undo / redo plugin only works with primitive data too
 * - This file allows us to support the metadata that we add
 *
 *
 * Undo / Redo flow:
 * 1. beforeChange - On a normal edit builds full Milanote CellData (within useTableCellEditingHandlers)
 * 2. beforeUndoStackChange / afterUndoStackChange - Attaches that data to HOT’s undo action
 * 3. beforeUndo / beforeRedo - Gets the undo / redo state & prepares it for use within the beforeChange
 * 4. beforeChange - Restores the full CellData from the previous step (within useTableCellEditingHandlers)
 * 5. afterChange - Clears the replay state as it's now been used (within useTableCellEditingHandlers)
 */
export const useTableUndoRedo = (
    hotInstance: Handsontable | null,
    undoRedoRichCellDataState: UndoRedoRichCellDataState,
): void => {
    useEffect(() => {
        if (!hotInstance) return;

        /**
         * Step 1 - within useTableCellEditingHandlers.
         * beforeChange - (at the very end) - adds the normalised Milanote CellData onto this table's
         *  undo/redo coordination state object
         *  via setPendingUndoRedoActionCellDataChanges.
         */

        /**
         * Step 2.
         * Tracks whether HOT is creating a new undo action so the corresponding Milanote cell data can be attached.
         */
        const beforeUndoStackChange: NonNullable<Events['beforeUndoStackChange']> = (_doneActions, source) => {
            setPendingUndoStackChangeSource(undoRedoRichCellDataState, source);
        };

        /**
         * Step 2.5
         * Mirrors Milanote cell data onto the newest HOT undo action after it is pushed onto the stack.
         */
        const afterUndoStackChange: NonNullable<Events['afterUndoStackChange']> = (
            _doneActionsBefore,
            doneActionsAfter,
        ) => {
            syncPendingUndoRedoActionToLatestUndoStackEntry(
                undoRedoRichCellDataState,
                doneActionsAfter as UndoRedoActionWithCellData[],
            );
        };

        /**
         * Step 3.
         * Prepares the previous cell data before HOT replays an undo change.
         */
        const beforeUndo: NonNullable<Events['beforeUndo']> = (action) => {
            startUndoRedoCellDataReplay(
                undoRedoRichCellDataState,
                TableChangeSource.UNDO,
                action as UndoRedoActionWithCellData,
            );
        };

        /**
         * Step 3.
         * Prepares the next rich cell data before HOT replays a redo change.
         */
        const beforeRedo: NonNullable<Events['beforeRedo']> = (action) => {
            startUndoRedoCellDataReplay(
                undoRedoRichCellDataState,
                TableChangeSource.REDO,
                action as UndoRedoActionWithCellData,
            );
        };

        /**
         * Step 4 - within useTableCellEditingHandlers.
         * beforeChange - (towards the start) - Uses getUndoRedoReplayCellData and createCellDataChangesFromReplay
         *  to restore the full Milanote CellData for undo/redo changes & sets it onto the metadata for the
         *  relevant cells (this is the same way that a normal edit would do it).
         */

        /**
         * Step 5 - within useTableCellEditingHandlers.
         * afterChange - Uses clearUndoRedoCellDataReplay so that the "replay data" won't get inadvertently
         *  used in another flow later.
         */

        /**
         * Handsontable's formulas plugin always calls HyperFormula's undo before HOT replays an undo action.
         * Some Milanote actions are stored only in HOT's custom undo stack, so there is no matching HyperFormula
         * operation to consume.
         * Suppress that one HyperFormula undo so the custom HOT replay remains the only operation applied.
         */
        const beforeUndoGlobal = createFormulaEngineUndoRedoSuppressionHook(hotInstance, 'undo');
        const beforeRedoGlobal = createFormulaEngineUndoRedoSuppressionHook(hotInstance, 'redo');

        const removeBeforeUndoGlobal = addGlobalHook('beforeUndo', beforeUndoGlobal);
        const removeBeforeRedoGlobal = addGlobalHook('beforeRedo', beforeRedoGlobal);

        hotInstance.addHook('beforeUndoStackChange', beforeUndoStackChange);
        hotInstance.addHook('afterUndoStackChange', afterUndoStackChange);
        hotInstance.addHook('beforeUndo', beforeUndo);
        hotInstance.addHook('beforeRedo', beforeRedo);

        return () => {
            removeBeforeUndoGlobal();
            removeBeforeRedoGlobal();
            hotInstance.removeHook('beforeUndoStackChange', beforeUndoStackChange);
            hotInstance.removeHook('afterUndoStackChange', afterUndoStackChange);
            hotInstance.removeHook('beforeUndo', beforeUndo);
            hotInstance.removeHook('beforeRedo', beforeRedo);
        };
    }, [hotInstance, undoRedoRichCellDataState]);
};

export default useTableUndoRedo;
